/**
 * CRON Interval Calculator
 * Uses cron-parser library for accurate CRON parsing
 */

import CronExpressionParser from 'cron-parser';

export interface CronParseResult {
  minutes: number;
  isValid: boolean;
  error?: string;
}

/**
 * Calculate the minimum interval between CRON executions
 * Uses cron-parser to get execution dates and calculate gaps
 *
 * @param cronExpr - Standard 5-field CRON expression
 * @returns CronParseResult with minimum interval in minutes
 */
export function calculateCronInterval(cronExpr: string): CronParseResult {
  const trimmed = cronExpr.trim();
  if (!trimmed) {
    return { minutes: 0, isValid: false, error: 'CRON expression is required' };
  }

  try {
    // Parse the CRON expression
    const interval = CronExpressionParser.parse(trimmed, {
      currentDate: new Date(),
      tz: 'UTC',
    });

    // Get next 10 execution dates to find minimum gap
    const dates: Date[] = [];
    for (let i = 0; i < 10; i++) {
      try {
        dates.push(interval.next().toDate());
      } catch {
        break;
      }
    }

    if (dates.length < 2) {
      return { minutes: 0, isValid: false, error: 'Could not determine execution times' };
    }

    // Calculate minimum gap between consecutive executions
    let minGapMinutes = Infinity;
    for (let i = 1; i < dates.length; i++) {
      const gapMs = dates[i].getTime() - dates[i - 1].getTime();
      const gapMinutes = gapMs / (1000 * 60);
      if (gapMinutes < minGapMinutes) {
        minGapMinutes = gapMinutes;
      }
    }

    return {
      minutes: Math.round(minGapMinutes),
      isValid: true,
    };
  } catch (err) {
    return {
      minutes: 0,
      isValid: false,
      error: err instanceof Error ? err.message : 'Invalid CRON expression',
    };
  }
}

/**
 * Validate a CRON expression against a minimum interval requirement
 *
 * @param cronExpr - CRON expression to validate
 * @param minIntervalMinutes - Minimum allowed interval in minutes
 * @returns Validation result with error message if invalid
 */
export function validateCronInterval(
  cronExpr: string,
  minIntervalMinutes: number
): { isValid: boolean; error?: string } {
  const result = calculateCronInterval(cronExpr);

  if (!result.isValid) {
    return { isValid: false, error: result.error };
  }

  if (result.minutes < minIntervalMinutes) {
    return {
      isValid: false,
      error: `CRON schedule has an effective interval of ${result.minutes} minutes, but your plan requires a minimum of ${minIntervalMinutes} minutes. Please upgrade for more frequent backups.`,
    };
  }

  return { isValid: true };
}
